import React, { Component } from "react"
import PropTypes from "prop-types"
import { Field } from "redux-form/immutable"

import { Alert } from "@quorumus/plum"

import CustomFieldFormSection from "app/static/frontend/custom-fields/containers/CustomFieldFormSection"
import GridToggleSelectField from "app/static/frontend/forms/components/GridToggleSelectField"

// generic component
class BulkActionsCustomField extends Component {
    static propTypes = {
        // from app/static/frontend/search-new/components/BulkActions/BulkActionsConsole
        change: PropTypes.func.isRequired,
        form: PropTypes.string.isRequired,
        quorumDataType: PropTypes.number.isRequired,
        // from app/static/frontend/search-new/containers/BulkActions/actions/BulkActionsCustomField
        customFieldNameDict: PropTypes.object.isRequired,
        customFieldSlug: PropTypes.string,
        customFieldTagType: PropTypes.number,
        customFieldType: PropTypes.number.isRequired,
    }

    componentDidUpdate(prevProps) {
        // update the form if the user has selected a custom field slug from the first GridToggleSelectField
        if (
            prevProps.customFieldSlug &&
            // and it differs from the previously selected custom field slug
            (prevProps.customFieldSlug !== this.props.customFieldSlug ||
                // and it differs from the previously selected custom field type (add/clear)
                prevProps.customFieldType !== this.props.customFieldType)
        ) {
            // remove that custom field slug value from the store so we can properly validate the form with the new selection
            this.props.change(prevProps.customFieldSlug, null)
            // If we're switching from a text/string tag type field that is hyperlinkable,
            // ensure any previous corresponding custom field external link field value
            // is cleared from the store
            if (this.props.customFieldNameDict[prevProps.customFieldSlug]?.is_hyperlinkable) {
                this.props.change(`${prevProps.customFieldSlug}_url`, null)
            }
        }
    }

    render() {
        const selectedCustomField = this.props.customFieldNameDict[this.props.customFieldSlug]
        const getDisabledBulkUploadMessage = () => {
            if (selectedCustomField?.display_on_create_page === false) {
                return (
                    <Alert variant="warning">
                        Custom fields with "Display on Create Page" set to "Do Not Display" are not possible to bulk
                        edit.
                    </Alert>
                )
            }
            if (selectedCustomField?.conditional_boolean_value) {
                return <Alert variant="warning">Conditional custom fields are not possible to bulk edit.</Alert>
            }
            return null
        }
        const disabledBulkUploadMessage = getDisabledBulkUploadMessage()
        return (
            <div className="bulk-actions-console-custom-fields-form" data-cy="bulk-actions-console-custom-fields-form">
                <Field
                    allowNull={false}
                    choices={Object.values(this.props.customFieldNameDict).map((value) => {
                        // we set the slug as the value and the name as the label
                        // so we can properly display the options in the GridToggleSelectField component
                        let appendToName = ""
                        if (!value.display_on_create_page)
                            appendToName =
                                " - (Custom fields with Display on Create Page set to false are not possible to bulk edit)"
                        if (value.conditional_boolean_value)
                            appendToName = " - (Conditional fields are not possible to bulk edit)"

                        return {
                            id: value.id,
                            label: value.name + appendToName,
                            value: value.slug,
                            isDisabled: value.display_on_create_page === false || value.conditional_boolean_value,
                        }
                    })}
                    className="bulk-actions-form-custom-field-select"
                    component={GridToggleSelectField}
                    dropUp={false}
                    label="Choose a Custom Field:"
                    multi={false}
                    name="custom_field_slug"
                    props={{
                        shouldNotShowClearButton: true,
                    }}
                />
                {disabledBulkUploadMessage}
                {selectedCustomField && !disabledBulkUploadMessage && (
                    <Field
                        allowNull={false}
                        choices={DjangIO.app.search.bulkactions.types.CustomFieldSelectType.items()
                            .filter((elem) => {
                                // only show the "Remove" button when a multi options list custom field type is selected
                                if (
                                    elem.value ===
                                    DjangIO.app.search.bulkactions.types.CustomFieldSelectType.remove.value
                                ) {
                                    if (
                                        this.props.customFieldTagType ===
                                        DjangIO.app.person.types.TagType.multi_options_list.value
                                    ) {
                                        return true
                                    }

                                    return false
                                }

                                return true
                            })
                            .map((elem) => {
                                const currentElement = elem

                                if (
                                    this.props.customFieldNameDict[this.props.customFieldSlug]
                                        ?.display_on_create_page === false
                                ) {
                                    currentElement.disabled = true
                                }

                                // change the name of the "Set Value" button to "Add Value(s)"
                                // and "Clear Value" to "Clear Value(s)"
                                // when a multi options list custom field type is selected
                                if (
                                    currentElement.value ===
                                        DjangIO.app.search.bulkactions.types.CustomFieldSelectType.add.value ||
                                    currentElement.value ===
                                        DjangIO.app.search.bulkactions.types.CustomFieldSelectType.clear.value
                                ) {
                                    if (
                                        this.props.customFieldTagType ===
                                        DjangIO.app.person.types.TagType.multi_options_list.value
                                    ) {
                                        return {
                                            ...currentElement,
                                            label: DjangIO.app.search.bulkactions.types.CustomFieldSelectType.by_value(
                                                currentElement.value,
                                            ).multi_select_label,
                                        }
                                    }
                                }

                                return currentElement
                            })
                            .sort(DjangIO.valueSort)}
                        className="bulk-actions-form-custom-field-type-select"
                        component={GridToggleSelectField}
                        dropUp={false}
                        label="Choose an Action Type:"
                        multi={false}
                        name="custom_field_type"
                    />
                )}
                {
                    // only show the custom field redux form if the user has selected a custom field slug from the GridToggleSelectField
                    this.props.customFieldSlug &&
                        this.props.customFieldNameDict &&
                        this.props.customFieldNameDict[this.props.customFieldSlug] &&
                        // and they are not clearing all values
                        this.props.customFieldType !==
                            DjangIO.app.search.bulkactions.types.CustomFieldSelectType.clear.value && (
                            <CustomFieldFormSection
                                form={this.props.form}
                                // the options for our singular custom field slug
                                // (we want to grab the slug that they have selected)
                                customFieldNameDict={{
                                    [this.props.customFieldSlug]:
                                        this.props.customFieldNameDict[this.props.customFieldSlug],
                                }}
                                // if the customFieldNameDict does not exist,
                                // we display a description about how that custom field has no values
                                dataType={DjangIO.app.models.QuorumDataType.by_value(this.props.quorumDataType).plural}
                            />
                        )
                }
            </div>
        )
    }
}

export default BulkActionsCustomField
